"use client";
import {
  addToWishlist,
  createWishlist,
  deleteWishlistLineItem,
} from "@/api/saleor/services/wishlist";
import { analyticEvents, handlePostAnalytics } from "@/apiUtils/analyticsUtils";
import { getWishlistIdByLocale } from "@/apiUtils/commonUtils";
import { analyticEventTypes } from "@/apiUtils/constants";
import {
  getFromCookieMetadataSA,
  setInCookieMetadataSA,
} from "@/apiUtils/cookieDataUtilsServerActions";
import CloseIcon from "@/assets/icons/close.svg?react";
import HeartIcon from "@/assets/icons/heartIcon.svg";
import { CheckoutLines, WishlistCreateResponse } from "@/common/lib/types";
import AddToWishlist from "@/components/productcard/AddToWishlist";
import Button from "@/components/ui/button/Button";
import CldImage from "@/components/ui/cldImg/CldImage";
import Modal from "@/components/ui/modal_new/Modal";
import useCRouter from "@/hooks/useCRouter";
import { useCustomToast } from "@/hooks/useCustomToast";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useGlobalStore } from "@/store/store";
import { getCloudinaryURL } from "@/utils/cloudinary";
import { getChannelIdInClient, getLangFromLocale } from "@/utils/common";
import { useParams } from "next/navigation";
import { useMemo, useState } from "react";
import NoImageSrc from "@/assets/images/no_image.svg";

interface IProps {
  cartItem: CheckoutLines;
  src: string;
  productUrl: string;
  variantId: string;
  staticText?: Record<string, string> | undefined;
  preview?: boolean;
  onRemove: () => void;
  isGiftPromotedProduct?: boolean;
}

const CartImage = ({
  cartItem,
  src,
  productUrl,
  variantId,
  staticText,
  preview = false,
  onRemove,
  isGiftPromotedProduct,
}: IProps) => {
  console.log("cart img", src);

  const { locale } = useParams();
  const router = useCRouter();
  const isSmallScreen = useMediaQuery("(max-width: 640px)", {
    defaultValue: false,
    initializeWithValue: true,
  });
  const lang = getLangFromLocale(locale as string);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const { wishlistedProducts, addProductToWishlist, removeProductFromWishlist, isAuthenticated } =
    useGlobalStore();
  const { showToast } = useCustomToast();

  const [isWishlistedLocal, setIsWishlisted] = useState<boolean | null>(null);
  const isWishlisted = isWishlistedLocal !== null ? isWishlistedLocal : !!wishlistedProducts?.includes(variantId);

  const itemHasPower = useMemo(() => {
    const item = cartItem?.variant?.attributes.find((item) => item.attribute.slug === "clPower");
    if (item) {
      const itemPower = Number(item?.values[0].plainText?.split(" ")[0] ?? "");
      if (itemPower != 0) {
        return true;
      }
    } else return false;
  }, [cartItem]);

  const handleOpenConfirmationModal = () => {
    setOpenConfirmation(true);
  };

  const handleCloseConfirmationModal = () => {
    setOpenConfirmation(false);
  };

  const handleDelete = () => {
    onRemove();
    handleCloseConfirmationModal();
  };

  const handleWishListClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isWishlisted) {
      handleRemoveFromWishlist();
      return;
    }

    await handleAddToWishlist();
  };

  const handleRemoveFromWishlist = async () => {
    setIsWishlisted(false);
    removeProductFromWishlist(variantId);

    try {
      const wishlistId: string = await getFromCookieMetadataSA(
        getWishlistIdByLocale(locale as string),
      );
      const response = await deleteWishlistLineItem(wishlistId, variantId);
      if (response?.wishlist) {
        showToast({
          text: staticText?.common_product_removed_from_wishlist_message ?? "",
          lang,
          icon: HeartIcon,
          redirectUrl: isAuthenticated ? "/my-account/favorites" : "/favorites",
        });
      } else {
        showToast({
          text: staticText?.common_add_to_cart_error_toast_message ?? "",
          lang,
          icon: HeartIcon,
        });
        setIsWishlisted(true);
        addProductToWishlist(variantId);
      }
    } catch (error) {
      setIsWishlisted(true);
      addProductToWishlist(variantId);
      showToast({
        text: staticText?.common_add_to_cart_error_toast_message ?? "",
        lang,
        icon: HeartIcon,
      });
    }
  };

  const handleAddToWishlist = async () => {
    setIsWishlisted(true);
    addProductToWishlist(variantId);
    console.log("inside add to wishlist");

    try {
      const wishlistId: string = await getFromCookieMetadataSA(
        getWishlistIdByLocale(locale as string),
      );

      if (wishlistId) {
        const response = await addToWishlist(wishlistId, variantId, true);

        if (response?.wishlist?.id) {
          showToast({
            text: staticText?.common_product_added_to_wishlist_message!,
            lang,
            icon: HeartIcon,
            redirectUrl: isAuthenticated ? "/my-account/favorites" : "/favorites",
          });

          setInCookieMetadataSA(getWishlistIdByLocale(locale as string), response.wishlist.id);

          handlePostAnalytics({
            eventType: analyticEventTypes.ACTIONS,
            data: {
              target: analyticEvents.ADD_TO_WISHLIST,
              lineItem: { ...cartItem, selectedQuantity: 1 },
            },
          });
        } else {
          showToast({
            text: staticText?.common_add_to_cart_error_toast_message ?? "",
            lang,
            icon: HeartIcon,
          });
          setIsWishlisted(false);
          removeProductFromWishlist(variantId);
        }
      } else {
        const channel = getChannelIdInClient(locale as string);
        const response: WishlistCreateResponse = await createWishlist(channel, [
          { variantId, isDefaultVariant: true },
        ]);
        if (response?.wishlist?.id) {
          showToast({
            text: staticText?.common_product_added_to_wishlist_message ?? "",
            lang,
            icon: HeartIcon,
            redirectUrl: isAuthenticated ? "/my-account/favorites" : "/favorites",
          });
          setInCookieMetadataSA(getWishlistIdByLocale(locale as string), response.wishlist.id);
          handlePostAnalytics({
            eventType: analyticEventTypes.ACTIONS,
            data: {
              target: analyticEvents.ADD_TO_WISHLIST,
              lineItem: { ...cartItem, selectedQuantity: 1 },
            },
          });
        }
      }
    } catch (error) {
      setIsWishlisted(false);
      removeProductFromWishlist(variantId);
      showToast({
        text: staticText?.common_add_to_cart_error_toast_message ?? "",
        lang,
        icon: HeartIcon,
      });
    }
  };

  const handleRedirectToPdp = () => {
    if (!productUrl) {
      return;
    }
    router.push(productUrl);
  };

  return (
    <>
      {!preview && !isGiftPromotedProduct && (
        <div className="absolute right-7 top-6 z-10 flex h-4 w-4 cursor-pointer items-center justify-center md:right-3 md:top-2">
          <button
            type="button"
            disabled={isGiftPromotedProduct ? true : false}
            onClick={handleOpenConfirmationModal}
          >
            <CloseIcon height={12} width={12} />
          </button>
        </div>
      )}
      <div
        className={`relative flex h-[6.75rem] w-full ${isGiftPromotedProduct || !productUrl ? "cursor-default" : "cursor-pointer"} items-center justify-center bg-tertiary-5 md:h-[9.825rem] md:max-w-[8.188rem]`}
        onClick={!isGiftPromotedProduct && productUrl ? handleRedirectToPdp : undefined}
      >
        {!preview && !isGiftPromotedProduct && (
          <div className="absolute left-2 top-2 z-[1] text-xs font-semibold">
            <AddToWishlist
              isWishlisted={isWishlisted}
              onAddToWishList={handleWishListClick}
              onCart
            />
          </div>
        )}
        {/* <div className="max-w-[12rem] md:max-w-[7.813rem]">
          <LazyImage
            src={src}
            width={125}
            height={158}
            alt={"productImage"}
            className={(_, isLoadingError) => cn("h-full w-full", !isLoadingError && "invert-[2%]")}
          />
        </div> */}
        {/* <LazyImage
          src={src}
          width={125}
          height={158}
          alt={"productImage"}
          className={(isLoading, isError) =>
            cn(
              "h-auto w-full",
              isLoading || !isError
                ? "max-w-[12rem] invert-[2%] md:max-w-[7.813rem]"
                : "h-full w-full",
            )
          }
        /> */}
        <div className="flex aspect-[0.84/1] w-[132px] items-center justify-center">
          <CldImage
            src={getCloudinaryURL(src) ?? NoImageSrc}
            width={125}
            height={108}
            alt={"productImage"}
            className="w-full invert-[2%]"
            unoptimized
          />
        </div>
      </div>

      <Modal
        onClose={() => setOpenConfirmation(false)}
        isOpen={openConfirmation}
        slots={{
          container: { className: "px-10 py-8 sm:px-6 sm:py-5" },
          close: {
            container: {
              className: "mb-0",
            },
            visible: true,
          },
        }}
      >
        <div className="flex flex-col gap-5 sm:flex-row">
          <div className="flex aspect-[130/157] h-[157px] items-center justify-center">
            <CldImage
              src={getCloudinaryURL(src) ?? NoImageSrc}
              width={125}
              height={158}
              alt={"productImage"}
              className="w-full pb-5"
            />
          </div>
          <div className="flex flex-col justify-between gap-5 sm:gap-0">
            <h3 className="max-w-[14rem] text-center text-sm font-semibold sm:text-start sm:text-base">
              {staticText?.cart_remove_item_confirmation_message || ""}
            </h3>
            <div className="flex flex-col items-center gap-4 sm:flex-row">
              <Button
                onClick={handleCloseConfirmationModal}
                className="order-2 flex h-9 max-w-[125px] items-center justify-center whitespace-nowrap text-[13px] sm:order-1"
                variant={isSmallScreen ? "link" : "outlined"}
              >
                {staticText?.cart_keep_item_link || ""}
              </Button>
              <Button
                className="order-1 flex h-9 max-w-[155px] items-center justify-center whitespace-nowrap text-[13px] sm:order-2"
                onClick={handleDelete}
                activateProgressOverlay={
                  cartItem?.variant?.product?.productType?.slug === "eyeglasses" || itemHasPower
                    ? true
                    : false
                }
                onAnimationEnd={handleDelete}
              >
                {staticText?.cart_remove_item_confirmation_button || ""}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CartImage;
